// Library Methods
import { useTranslation } from "react-i18next";
import { useCallback, useMemo } from "react";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TimezoneDropdown from "../../shared/TimezoneDropdown/TimezoneDropdown";
import Alert from "src/components/shared/alerts/Alert/Alert";
import TitleWithTooltip from "src/components/shared/text/TitleWithTooltip/TitleWithTooltip";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useTheme } from "@mui/material/styles";
import CustomSwitch from "src/components/shared/inputs/CustomSwitch/CustomSwitch";

// Hooks
import { useClinicById } from "src/services/clinics/hooks/useClinicById";

// Contexts
import { useClinicSettingsContext } from "src/contexts/clinic/ClinicSettingsContext";
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Constants
import { ONTARIO_TIMEZONES } from "src/constants/enums";

// Styles
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

const getSwitchLabelSx = (mutedText: string) => ({
  marginRight: 0,
  "& .MuiFormControlLabel-label": {
    fontSize: "14px",
    marginLeft: "0px",
    color: mutedText,
    "&.Mui-disabled": { color: mutedText },
  },
});

const TimezoneSetup = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { mutedText } = getSurfaceTokens(theme);
  const { clinicInfo } = useClinicContext();

  const { data: clinicData } = useClinicById({
    useToken: true,
    clinicId: clinicInfo?.id,
  });

  const { currentSettings, handleUpdateSettings, isLoading, isUpdating } =
    useClinicSettingsContext();

  const MTOFixationMethod = useMemo(
    () => Boolean(currentSettings?.MTOFixationMethod),
    [currentSettings?.MTOFixationMethod]
  );

  const handleUpdateMTOSwitch = useCallback(
    (inputValue: boolean) => {
      handleUpdateSettings({
        ...currentSettings,
        MTOFixationMethod: inputValue,
      });
    },
    [handleUpdateSettings, currentSettings]
  );

  return (
    <Box display="flex" flexDirection="column" gap="12px">
      <Alert
        variant="info"
        alertContent={
          <Typography fontSize="14px">
            {t(
              "Please select a major city that corresponds to the timezone in your location."
            )}
          </Typography>
        }
      />

      <Box display="flex" flexDirection="column" gap="6px">
        <TitleWithTooltip
          title={t("Clinic Timezone")}
          tooltip={t("The timezone used to display dates on this site.")}
          titleStyle={{ fontSize: "14px", fontWeight: 600 }}
        />
        <TimezoneDropdown />
      </Box>

      {/* MTO Report Configuration */}
      {ONTARIO_TIMEZONES.includes(clinicData?.timezone ?? "") && (
        <Box display="flex" flexDirection="column" gap="6px">
          <TitleWithTooltip
            title={t("MTO Report Configuration")}
            tooltip={t(
              "Displays exam reports in the format required by the Ontario Ministry of Transportation (MTO)."
            )}
            titleStyle={{ fontSize: "14px", fontWeight: 600 }}
          />
          <Alert
            variant="warning"
            alertContent={
              <Typography fontSize="14px">
                {t(
                  "Only enable this setting if your clinic is based in Ontario."
                )}
              </Typography>
            }
          />
          <FormControlLabel
            sx={getSwitchLabelSx(mutedText)}
            control={
              <CustomSwitch
                disabled={isLoading || isUpdating}
                checked={MTOFixationMethod}
                onChange={(e) => handleUpdateMTOSwitch(e.target.checked)}
                sx={{
                  ...(theme.palette.mode === "dark" && {
                    "& .MuiSwitch-track": {
                      backgroundColor: "#202338",
                    },
                    "& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track": {
                      backgroundColor: "#20233843",
                    },
                  }),
                }}
              />
            }
            label={t("Enable MTO format for Esterman exams")}
          />
        </Box>
      )}
    </Box>
  );
};

export default TimezoneSetup;
